import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import {
  ExpenseReminderNotification,
  CreateExpenseRequest,
  Expense,
} from '@/types';
import { ExpensesService } from '@/utils/expenses-service';

interface ExpenseReminderModalProps {
  open: boolean;
  onClose: () => void;
  reminder: ExpenseReminderNotification | null;
  onExpenseCreated?: (expense: Expense) => void;
  onReminderDismissed?: (reminderId: string) => void;
}

interface ExpenseFormData {
  amount: number;
  description: string;
  date: string;
  receiptUrl?: string;
}

const ExpenseReminderModal: React.FC<ExpenseReminderModalProps> = ({
  open,
  onClose,
  reminder,
  onExpenseCreated,
  onReminderDismissed,
}) => {
  // Form state
  const [formData, setFormData] = useState<ExpenseFormData>({
    amount: 0,
    description: '',
    date: new Date().toISOString().split('T')[0], // Today's date
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Reset form when reminder changes or modal opens
  useEffect(() => {
    if (reminder && open) {
      setFormData({
        amount: reminder.estimatedAmount ? 
          (reminder.estimatedAmount.min + reminder.estimatedAmount.max) / 2 : 0,
        description: `${reminder.categoryName} expense`,
        date: reminder.dueDate,
      });
      setError(null);
      setSuccess(false);
    }
  }, [reminder, open]);

  const handleInputChange = (field: keyof ExpenseFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reminder) return;

    // Validate form
    if (!formData.amount || formData.amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!formData.date) {
      setError('Please select a date');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create expense request
      const expenseRequest: CreateExpenseRequest = {
        categoryId: reminder.recurringExpenseId, // This should be the category ID from the reminder
        amount: formData.amount,
        currency: 'USD', // Default currency, could be made configurable
        description: formData.description.trim() || undefined,
        date: formData.date,
        receiptUrl: formData.receiptUrl,
        recurrenceType: 'variable_recurring',
      };

      // Complete the expense reminder
      const createdExpense = await ExpensesService.completeExpenseReminder(
        reminder.reminderId,
        expenseRequest
      );

      setSuccess(true);
      onExpenseCreated?.(createdExpense);

      // Close modal after a brief delay to show success message
      setTimeout(() => {
        handleClose();
      }, 1500);

    } catch (err) {
      console.error('Failed to create expense:', err);
      setError('Failed to save expense. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (!reminder) return;

    try {
      setLoading(true);
      await ExpensesService.dismissExpenseReminder(reminder.reminderId);
      onReminderDismissed?.(reminder.reminderId);
      handleClose();
    } catch (err) {
      console.error('Failed to dismiss reminder:', err);
      setError('Failed to dismiss reminder. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        amount: 0,
        description: '',
        date: new Date().toISOString().split('T')[0],
      });
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const formatCurrency = (amount: number) => {
    return ExpensesService.formatCurrency(amount, 'USD');
  };

  const getEstimatedRangeText = () => {
    if (!reminder?.estimatedAmount) return null;
    
    const { min, max } = reminder.estimatedAmount;
    if (min === max) {
      return `Estimated: ${formatCurrency(min)}`;
    }
    return `Estimated: ${formatCurrency(min)} - ${formatCurrency(max)}`;
  };

  if (!reminder) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Expense Reminder
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Success Message */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Expense saved successfully! The reminder has been completed.
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Reminder Information Card */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    {reminder.categoryName}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Due Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {format(parseISO(reminder.dueDate), 'MMM dd, yyyy')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {reminder.estimatedAmount && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <MoneyIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Estimated Amount
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {getEstimatedRangeText()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              {reminder.listingTitle && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ReceiptIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Property
                      </Typography>
                      <Chip 
                        label={reminder.listingTitle} 
                        size="small" 
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Divider sx={{ mb: 3 }} />

        {/* Quick Entry Form */}
        <Typography variant="h6" gutterBottom>
          Enter Expense Details
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Amount Input */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Amount"
                value={formData.amount || ''}
                onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 },
                }}
                disabled={loading}
                autoFocus
              />
            </Grid>

            {/* Date Input */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Grid>

            {/* Description/Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes (Optional)"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Add any additional details about this expense..."
                disabled={loading}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleDismiss}
          disabled={loading}
          color="inherit"
        >
          Dismiss Reminder
        </Button>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.amount || formData.amount <= 0}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Saving...' : 'Save Expense'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseReminderModal;